import { Accessor, createEffect, createSignal, Setter, Show } from "solid-js";
import { Spacer } from "../../widgets/spacer";
import { BackButton, ButtonRegular } from "../../widgets/button";
import { createJob } from "~/utils/job";
import { confirmPhoneUpdate, initiatePhoneUpdate } from "~/data/products";
import { NumberKeyboard } from "../voucher_edit/number_keyboard";

export function PhoneOtpVerificationModal(props: {
  phoneNumber: string;
  otpToken: string;
  onPhoneVerified: () => void;
}) {
  const [otp, setOtp] = createSignal<string>("");
  const [otpToken, setOtpToken] = createSignal<string>(props.otpToken);
  const [errorMessage, setErrorMessage] = createSignal<string | null>("");
  const [resendTimeLeft, setResendTimeLeft] = createSignal<number>(30);
  let resendInterval: NodeJS.Timeout | undefined;

  const verifyOtpJob = createJob({
    errorCallback: (error) => {
      setOtp("");
      setErrorMessage(error.message);
    },
    initialJob: async () => {
      if (otp().length !== 6) {
        throw new Error("Please enter a valid 6-digit OTP");
      }
      return confirmPhoneUpdate(otp(), otpToken());
    },
    successCallback: async () => {
      // Return to the original flow after successful verification
      props.onPhoneVerified();
    },
  });

  const resendOtpJob = createJob({
    errorCallback: (error) => {
      setErrorMessage(error.message);
    },
    initialJob: async () => {
      return initiatePhoneUpdate(props.phoneNumber);
    },
    successCallback: async (response) => {
      setOtpToken(response.otpToken);
      startResendTimer();
    },
  });

  // Start the resend timer when component mounts
  createEffect(() => {
    startResendTimer();

    return () => {
      if (resendInterval) {
        clearInterval(resendInterval);
      }
    };
  });

  // Check if OTP is complete (6 digits) and automatically verify
  createEffect(() => {
    if (otp().length === 6) {
      verifyOtpJob.run();
    }
    setErrorMessage(null);
  });

  function startResendTimer() {
    if (resendInterval) {
      clearInterval(resendInterval);
    }

    setResendTimeLeft(30);

    resendInterval = setInterval(() => {
      setResendTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(resendInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  return (
    <div class="flex h-full flex-col">
      <div class="flex h-full flex-col bg-dividerLight p-5">
        <BackButton />
        <div class="mt-[40px]"></div>

        <div class="mb-2 text-h3 text-textDark">Enter OTP</div>
        <div class="mb-6 text-normal text-textNormal">
          OTP has been sent to{" "}
          <span class="font-semibold">+91 {props.phoneNumber}</span>
        </div>

        <div class="flex w-full flex-col items-start">
          <div class="w-full">
            <OtpInput error={errorMessage} otp={otp} setOtp={setOtp} />
          </div>
          <div class="h-[50px]"></div>
          <Show when={errorMessage()}>
            <Spacer height={4} />
            <span class="text-medium text-errorDark">{errorMessage()}</span>
          </Show>

          <Spacer height={16} />

          <div class="flex justify-center">
            {resendTimeLeft() > 0 ? (
              <span class="text-medium text-textNormal">
                Resend OTP in {resendTimeLeft()} seconds
              </span>
            ) : (
              <span
                class="cursor-pointer text-medium text-basePrimaryDark"
                onClick={() => !resendOtpJob.isRunning() && resendOtpJob.run()}
              >
                {resendOtpJob.isRunning() ? "Sending..." : "Resend OTP"}
              </span>
            )}
          </div>
        </div>

        <Spacer height={16} />
        <div class="grow"></div>

        <ButtonRegular
          onClick={() => verifyOtpJob.run()}
          isLoading={verifyOtpJob.isRunning()}
          isEnabled={otp().length === 6}
          class="button-filled mt-5"
        >
          Continue
        </ButtonRegular>
      </div>
    </div>
  );
}

export function OtpInput(props: {
  otp: Accessor<string>;
  setOtp: Setter<string>;
  error: Accessor<string | null>;
}) {
  const [hasFocus, setHasFocus] = createSignal<boolean>(false);

  let otpInput: HTMLInputElement | undefined;
  createEffect(async () => {
    otpInput?.focus();
  });

  return (
    <div class="relative">
      <input
        ref={otpInput}
        class="absolute z-10 h-16 w-full bg-transparent text-baseTertiaryLight caret-transparent"
        type="tel"
        maxlength={6}
        value={props.otp()}
        placeholder=""
        onInput={(e) => props.setOtp(e.currentTarget.value)}
        onFocus={() => setHasFocus(true)}
        onBlur={() => setHasFocus(false)}
      />
      <div class=" pointer-events-none absolute z-20 flex w-full items-center justify-around space-x-2">
        {[...Array(6)].map((_, index) => (
          <div
            class={`flex h-12 w-16 cursor-text items-center justify-center rounded-[12px] border bg-white  ${
              props.error() == null
                ? index == props.otp().length
                  ? "border-[#000]"
                  : "border-baseSecondaryLight"
                : "border-errorDark"
            }`}
          >
            {props.otp()[index] ? (
              props.otp()[index]
            ) : index == props.otp().length && hasFocus() ? (
              <div class={`animateBlink h-3 w-0.5 bg-baseDark`} />
            ) : (
              <div class="h-1 w-1 rounded-full bg-baseSecondaryLight" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
